/**
 * 🌲 DREAM FOREST DUNGEON — Vertical-slice prototype
 *
 * Feb 28, 2026 — playable proof-of-concept of the first-user solo
 * dungeon. Ships with PLACEHOLDER visuals so the owner can see the
 * loop working before we lock final art / mobs / dialogue.
 *
 * Design canon:
 *   /app/memory/FIRST_USER_SOLO_DUNGEON_DREAM_FOREST_FEB2026.md
 *   /app/memory/DUNGEON_GRADING_AND_REWARDS_SYSTEM_RULE_FEB2026.md
 *   /app/memory/DUNGEON_BOSSES_ARE_PRECURSORS_SYSTEM_RULE_FEB2026.md
 *
 * PHASE 1 SLICE INCLUDES:
 *   • 2D top-down room (with follow-cam that pans as player walks)
 *   • Class-picker header (Mage / Warrior / Ash Rogue / Archer)
 *   • Dual input (joystick + WASD + tap-to-move)
 *   • Loot box in Room 1 (opens on proximity → Chit Pouch choice modal)
 *   • Mob patrols (3 packs of Soot Birds) with aggro chase + leash
 *   • Barrier separating Room 1 → Room 2 (visible Gorgonax behind)
 *   • Barrier drop cinematic on quest completion
 *   • Room 2: Gorgonax + 2 Hatchbacks encounter
 *   • Mini-combat modal (tap-to-attack tutorial)
 *   • Grade screen with Overkill % → grade band + 1 card draw
 *
 * INTENTIONAL PLACEHOLDERS (locked art / final numbers pending):
 *   • Emoji sprites instead of authored art
 *   • Simplified mini-combat (owner will drop the tap rhythm)
 *   • Weak boss ultimate is a plain damage tick (no name — owner
 *     canon: no boss ultimate has a name yet)
 *   • Barrier is a CSS shimmer, no cinematic beyond a fade
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import SkillPicker from "@/components/combat/SkillPicker";

// 🌲 API base — grants hero-class EXP server-side on Dream Forest kills.
// Owner canon Mar 4, 2026: regular mob = +50 · Tier-1 boss = +1000
// (scales via `previous × 2 + 500` past Tier 4). SOLO only — MPD flow
// will follow its own curve.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ────────────────────── Constants ──────────────────────
const ROOM = { w: 24, h: 16 };  // 24×16 grid tiles, follow-cam pans within
const TILE = 40;                 // rendered pixel size
const CLASSES = [
  { id: "mage",       label: "Mage",      glyph: "🔮", weapon: "🪄" },
  { id: "warrior",    label: "Warrior",   glyph: "⚔️", weapon: "🗡️" },
  { id: "ash_rogue",  label: "Ash Rogue", glyph: "🌫️", weapon: "🗡️" },
  { id: "archer",     label: "Archer",    glyph: "🏹", weapon: "🏹" },
];
const OVERKILL_DEFAULT_MULT = 1.5;

// ────────────────────── Utilities ──────────────────────
const clamp  = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const dist   = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const rand   = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

// ────────────────────── Component ──────────────────────
// 🌲 Feb 2026 owner canon (#18 revised): the Dream Forest chit pouch
// grants TWO separate loots — a 500-Chit bag AND one temporary random
// stat boost (Dream Forest only). The boost's identity is REVEALED on
// pickup. Boost pool mirrors the 7 in-game stats (matches Mother
// Phoenix Crystal stat list) so nothing invents new mechanics.
const DREAM_FOREST_BOOSTS = [
  // 🌲 Feb 2026 owner spec (#27 / B27): NEVER label where a temp boost
  // works — the "Dream Forest only" tag spoils discovery. The details
  // below stay generic so the modal can't disclose the area restriction.
  { id: "power",      icon: "⚔️", label: "Power +10%",     detail: "Temporary boost" },
  { id: "intellect",  icon: "🔮", label: "Intellect +10%", detail: "Temporary boost" },
  { id: "pdef",       icon: "🛡️", label: "PDEF +10%",      detail: "Temporary boost" },
  { id: "mdef",       icon: "🧿", label: "MDEF +10%",      detail: "Temporary boost" },
  { id: "endurance",  icon: "❤️", label: "Endurance +10%", detail: "Temporary boost" },
  { id: "critical",   icon: "🎯", label: "Critical +5%",   detail: "Temporary boost" },
  { id: "block_crit", icon: "🪨", label: "Block Crit +5%", detail: "Temporary boost" },
];

function rollDreamForestBoost() {
  return DREAM_FOREST_BOOSTS[Math.floor(Math.random() * DREAM_FOREST_BOOSTS.length)];
}

export default function DreamForestDungeon() {
  const nav = useNavigate();
  const [phase, setPhase]     = useState("class_pick"); // class_pick → playing → combat → grade
  const [heroClass, setClass] = useState(null);
  const [runState, setRunState] = useState(null);
  const [combat, setCombat]     = useState(null); // {mob, hp, ...} while in mini-combat
  const [gradeOut, setGrade]    = useState(null); // grade result
  // Rolled ONCE at run start so the modal reveal matches the toast +
  // stored reward. Re-rolled on each new run via `startRun`.
  const [pouchBoost, setPouchBoost] = useState(() => rollDreamForestBoost());
  // 🌲 Mar 4, 2026 owner canon — first run is a PREP RUN that stops at
  // the boss barrier. `bossUnlocked=false` until the player has
  // completed ≥ 1 run. Fetched from /api/dream-forest/state on mount.
  const [bossUnlocked, setBossUnlocked] = useState(false);
  const [prepRunFinished, setPrepRunFinished] = useState(false);

  // Pull the player's Dream Forest progress up-front so the first-run
  // gate is deterministic before combat begins.
  useEffect(() => {
    axios.get(`${API}/dream-forest/state`)
      .then((r) => setBossUnlocked(!!r?.data?.boss_unlocked))
      .catch(() => setBossUnlocked(false));
  }, []);

  // 🩹 Oct 2026 owner canon (Fix #28 resend): the class picker is NEVER
  // shown inside the dungeon. The user already picked their class at
  // signup; we read it from /me and start the run immediately. If the
  // profile somehow has no class, we silently fall back to Warrior so
  // the loop never pauses — the picker lives at /hero-class if the
  // player wants to change later.
  useEffect(() => {
    if (phase !== "class_pick" || heroClass) return;
    axios.get(`${API}/me`)
      .then((r) => {
        const prof   = r?.data?.user || r?.data || {};
        const cls_id = (prof.hero_class || prof.class_id || prof.role_class || "").toString().toLowerCase();
        const found  = CLASSES.find((c) => c.id === cls_id);
        startRun(found || CLASSES[0]);
      })
      .catch(() => startRun(CLASSES[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, heroClass]);

  // ─── Kick off a run ───
  const startRun = (cls) => {
    setClass(cls);
    setPouchBoost(rollDreamForestBoost());
    setRunState({
      player: { x: 2, y: 8, hp: 200, max_hp: 200, atk: 45 },
      mobs: buildMobRoster(),
      barrierOpen: false,
      questsCleared: [],
      lootBox: { x: 1, y: 15, opened: false },
      bossArea: { x: 20, y: 8 },
      startTime: Date.now(),
      turnsTaken: 0,
      overkills: 0,
      totalKills: 0,
      damageTaken: 0,
      chitsEarned: 0,
      pouchReward: null,
    });
    setPhase("playing");
  };

  const onMobDefeated = (mobId, killingBlowDmg) => {
    setRunState((rs) => {
      const mob = rs.mobs.find((m) => m.id === mobId);
      const overkillThreshold = mob.max_hp * OVERKILL_DEFAULT_MULT;
      const overkilled = killingBlowDmg >= overkillThreshold;
      return {
        ...rs,
        mobs: rs.mobs.filter((m) => m.id !== mobId),
        questsCleared: [...rs.questsCleared, `defeat:${mobId}`],
        totalKills: rs.totalKills + 1,
        overkills: rs.overkills + (overkilled ? 1 : 0),
      };
    });
    setCombat(null);
    // 🌲 Mar 4, 2026 owner canon — grant +50 hero-class EXP per non-boss
    // Dream Forest kill. Fire-and-forget so a network hiccup can't
    // stall the FE dungeon loop; the toast surfaces successful grants
    // (incl. any level-up) so the player sees the payoff live.
    axios.post(`${API}/dream-forest/mob-defeated`).then((r) => {
      const d = r?.data || {};
      const parts = [`+${d.amount || 50} XP`];
      if ((d.leveled_up || []).length) parts.push(`🎉 Lv ${d.level}!`);
      toast.success(parts.join(" · "), { duration: 2500 });
    }).catch(() => { /* silent — client XP display syncs on next state fetch */ });
  };

  const onBossDefeated = (killingBlowDmg) => {
    setRunState((rs) => {
      const bossMax = 600;
      const overkilled = killingBlowDmg >= bossMax * OVERKILL_DEFAULT_MULT;
      return {
        ...rs,
        totalKills: rs.totalKills + 1,
        overkills: rs.overkills + (overkilled ? 1 : 0),
      };
    });
    setCombat(null);
    // 🌲 Mar 4, 2026 owner canon — grant tier-scaled boss EXP. Tier 1
    // = 1000, Tier 2 = 1000, Tier 3 = 3000, Tier 4 = 6500, Tier 5+ =
    // `previous × 2 + 500`. Currently only Tier 1 exists (Gorgonax);
    // future dream-forest tiers will pass dungeon_tier: 2/3/4+.
    // ALSO fires the Phoenix Reward — every dungeon win rolls the
    // Phoenix Hatch prize table (owner canon Mar 4, 2026).
    axios.post(`${API}/dream-forest/boss-defeated`, { dungeon_tier: 1 }).then((r) => {
      const d = r?.data || {};
      const parts = [`+${(d.amount || 1000).toLocaleString()} XP (boss)`];
      if ((d.leveled_up || []).length) parts.push(`🎉 Lv ${d.level}!`);
      toast.success(parts.join(" · "), { duration: 4500 });
      // 🐦‍🔥 Phoenix Reward — surface the drop as a second, bigger toast
      // so the dungeon feels like a proper payoff moment.
      const px = d.phoenix_reward?.drop;
      if (px) {
        const amt = px.amount ? ` (+${px.amount.toLocaleString()})` : "";
        toast.success(
          `🐦‍🔥 Phoenix Reward — ${px.icon || "🎁"} ${px.label || px.name || "Prize"}${amt}`,
          {
            description: px.description || "Claimed to your Phoenix Pouch.",
            duration: 7000,
          },
        );
      }
    }).catch(() => { /* silent */ });
    // Compute grade + finish run
    const rs = runStateRef.current;
    const totalKills = (rs.totalKills || 0) + 1;
    const overkills  = rs.overkills + ((killingBlowDmg >= 600 * OVERKILL_DEFAULT_MULT) ? 1 : 0);
    const overkillPct = totalKills > 0 ? overkills / totalKills : 0;
    const timeSec = (Date.now() - rs.startTime) / 1000;
    const hpPct   = rs.player.hp / rs.player.max_hp;
    const grade = computeGrade(overkillPct, hpPct, timeSec);
    const cardDraw = drawCard(grade);
    setGrade({ grade, overkillPct, hpPct, timeSec, overkills, totalKills, cardDraw });
    setPhase("grade");
  };

  // Ref for latest runState (avoid stale closures in async callbacks)
  const runStateRef = useRef(runState);
  useEffect(() => { runStateRef.current = runState; }, [runState]);

  // ─── UI phase router ───
  // 🩹 Oct 2026 (#28 resend) — the class picker NEVER renders inside
  // the dungeon. The user already picked their class at signup; the
  // dungeon simply reads it from the profile and starts the run.
  if (phase === "class_pick") return (
    <div className="min-h-screen bg-[#050B0A] text-neutral-500 flex items-center justify-center" data-testid="dream-forest-loading">
      Loading the Dream Forest…
    </div>
  );
  if (phase === "grade")      return <GradeScreen result={gradeOut} onExit={() => nav("/")} />;

  return (
    <div className="min-h-screen bg-[#050B0A] text-white overflow-hidden" data-testid="dream-forest-dungeon">
      <DungeonHUD run={runState} heroClass={heroClass} onExit={() => nav(-1)} />
      <DreamForestScene
        run={runState}
        setRun={setRunState}
        heroClass={heroClass}
        paused={!!combat || runState.lootBox.opened === "picking" || prepRunFinished}
        bossUnlocked={bossUnlocked}
        onPrepRunFinish={() => {
          // 🌲 First-run gate — clear conditions met but boss is locked.
          // Bump the run counter server-side (so next visit unlocks the
          // boss), then route to a friendly grade-screen variant.
          if (prepRunFinished) return;   // one-shot guard
          setPrepRunFinished(true);
          axios.post(`${API}/dream-forest/first-run-complete`).catch(() => {});
          const rs = runStateRef.current;
          const timeSec = (Date.now() - rs.startTime) / 1000;
          const hpPct   = rs.player.hp / rs.player.max_hp;
          setGrade({
            grade:       "PREP_RUN",
            prep_run:    true,
            hpPct, timeSec,
            overkillPct: rs.totalKills > 0 ? rs.overkills / rs.totalKills : 0,
            overkills:   rs.overkills,
            totalKills:  rs.totalKills,
            cardDraw:    null,
          });
          setPhase("grade");
        }}
        onOpenLootBox={() => setRunState((rs) => ({ ...rs, lootBox: { ...rs.lootBox, opened: "picking" } }))}
        onEnterCombat={(mob) => setCombat({ mob, playerHp: runState.player.hp, mobHp: mob.hp })}
        onEnterBoss={() => setCombat({ isBoss: true, mob: { name: "Gorgonax", icon: "🐲", hp: 600, max_hp: 600, atk: 40 }, minions: [{ id: "hb1", name: "Hatchback", icon: "🐣", hp: 80 }, { id: "hb2", name: "Hatchback", icon: "🐣", hp: 80 }], playerHp: runState.player.hp, mobHp: 600 })}
      />
      {runState.lootBox.opened === "picking" && (
        <ChitPouchModal
          boost={pouchBoost}
          onPick={(choice) => {
            // 🩹 Oct 2026 owner canon (Fix #26): separate pickups. Base
            // +150 Chits always granted; the player then chooses EITHER
            // +500 more Chits OR a Lv 1 Temporal Booster — not both.
            const baseChits = 150;
            setRunState((rs) => ({
              ...rs,
              lootBox: { ...rs.lootBox, opened: true },
              questsCleared: [...rs.questsCleared, "loot:pouch"],
              chitsEarned: rs.chitsEarned + baseChits + (choice === "chits" ? 500 : 0),
              pouchReward: choice === "booster" ? pouchBoost.id : null,
            }));
            if (choice === "booster") {
              toast.success(`💰 +${baseChits} Chits · ${pouchBoost.icon} ${pouchBoost.label} boost!`);
            } else {
              toast.success(`💰 +${baseChits + 500} Chits in the pouch!`);
            }
          }}
        />
      )}
      {combat && (
        <MiniCombatModal
          combat={combat}
          setCombat={setCombat}
          playerAtk={runState.player.atk}
          onWin={(killingBlowDmg) => {
            // onMobDefeated / onBossDefeated below already run their own
            // overkill check (they know the mob's max_hp), so we DON'T
            // double-increment here even though the child fired onOverkill.
            if (combat.isBoss) onBossDefeated(killingBlowDmg);
            else onMobDefeated(combat.mob.id, killingBlowDmg);
          }}
          onOverkill={({ mob, dmg }) => {
            toast.success(`💥 OVERKILL — ${dmg} dmg vs ${mob.max_hp} MAX HP`, { duration: 2500 });
          }}
          onDamage={(dmg) => setRunState((rs) => ({
            ...rs,
            player: { ...rs.player, hp: Math.max(0, rs.player.hp - dmg) },
            damageTaken: rs.damageTaken + dmg,
          }))}
          onFlee={() => {
            // 🏃 Shove player 4 tiles opposite the mob so proximity doesn't
            // instantly re-trigger combat when the modal closes. Also
            // teleport the mob back to its leash origin so it patrols
            // instead of immediately chasing.
            setRunState((rs) => {
              const mob = combat.mob;
              const dx = rs.player.x - mob.x;
              const dy = rs.player.y - mob.y;
              const d  = Math.hypot(dx, dy) || 1;
              const px = clamp(rs.player.x + (dx / d) * 4, 0.5, ROOM.w - 0.5);
              const py = clamp(rs.player.y + (dy / d) * 4, 0.5, ROOM.h - 0.5);
              const mobs = rs.mobs.map((m) =>
                m.id === mob.id ? { ...m, x: m.leashOrigin.x, y: m.leashOrigin.y, state: "returning" } : m
              );
              return { ...rs, player: { ...rs.player, x: px, y: py }, mobs };
            });
            setCombat(null);
            toast("🏃 You slipped away.", { duration: 1200 });
          }}
        />
      )}
    </div>
  );
}

// ────────────────────── Mob roster builder ──────────────────────
function buildMobRoster() {
  // 3 patrols of Soot Bird (canon: reused from hatch onboarding flow) —
  // Lv 1-3. Boss chamber uses Hatchbacks (see below). Emoji 🐤 → sooty
  // fledgling read; will swap for real illustration in later polish pass.
  return [
    { id: "soot1", name: "Soot Bird",  icon: "🐤", lv: 1, x:  6, y:  4, hp: 90, max_hp: 90, atk: 12,
      patrol: [{x:6,y:4},{x:8,y:4},{x:8,y:6},{x:6,y:6}], patrolIdx: 0, aggroRadius: 3, leashOrigin: { x: 6, y: 4 }, leashRadius: 6 },
    { id: "soot2", name: "Soot Bird",  icon: "🐤", lv: 2, x: 12, y: 11, hp: 130, max_hp: 130, atk: 16,
      patrol: [{x:12,y:11},{x:14,y:11},{x:14,y:9},{x:12,y:9}], patrolIdx: 0, aggroRadius: 3, leashOrigin: { x: 12, y: 11 }, leashRadius: 6 },
    { id: "soot3", name: "Soot Bird",  icon: "🐤", lv: 3, x: 16, y:  4, hp: 180, max_hp: 180, atk: 22,
      patrol: [{x:16,y:4},{x:18,y:4},{x:18,y:6},{x:16,y:6}], patrolIdx: 0, aggroRadius: 3, leashOrigin: { x: 16, y: 4 }, leashRadius: 6 },
  ];
}

// ────────────────────── Grade + Card draw ──────────────────────
function computeGrade(overkillPct, hpPct, timeSec) {
  // Draft mapping per DUNGEON_GRADING scribe
  if (overkillPct >= 1.0 && hpPct > 0.8) return "SSS";
  if (overkillPct >= 0.5 && hpPct > 0.6) return "SS";
  if (overkillPct >= 0.25)                return "S";
  if (hpPct > 0.4)                        return "A";
  if (hpPct > 0.15)                       return "B";
  return "C";
}
const GRADE_POOL = {
  SSS: { pool: "Mythic+ (exclusive)", card: "🌌 Cosmic Fragment ⭐⭐⭐",  color: "#FCD34D" },
  SS:  { pool: "Mythic",              card: "🩸 Blood-Glass Shard",       color: "#F472B6" },
  S:   { pool: "Premium",             card: "🔥 Legendary Ember",         color: "#F87171" },
  A:   { pool: "Standard",            card: "💰 100 Chits + 2 OD",        color: "#60A5FA" },
  B:   { pool: "Standard",            card: "💰 50 Chits",                color: "#60A5FA" },
  C:   { pool: "Basic",               card: "💧 10 Chits",                color: "#A3A3A3" },
  D:   { pool: "Basic",               card: "💧 5 Chits",                 color: "#A3A3A3" },
};
function drawCard(grade) { return GRADE_POOL[grade] || GRADE_POOL.C; }

// ────────────────────── Class picker ──────────────────────
function ClassPicker({ onPick, onBack }) {
  return (
    <div className="min-h-screen bg-[#050B0A] text-white flex items-center justify-center p-6" data-testid="class-picker">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E]">Dream Forest · Solo Dungeon</div>
          <h1 className="font-display text-4xl mt-2">Pick Your Class</h1>
          <p className="text-xs text-neutral-500 mt-2">The one you chose in onboarding. This is a proof-of-concept — final class-specific weapon+armor overlays are pending.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {CLASSES.map((c) => (
            <button
              key={c.id}
              data-testid={`class-pick-${c.id}`}
              onClick={() => onPick(c)}
              className="bg-[#0F0F0F] border-2 border-[#22C55E]/40 hover:border-[#22C55E] p-5 text-left transition"
            >
              <div className="text-4xl">{c.glyph}</div>
              <div className="font-bold text-lg mt-2">{c.label}</div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">weapon: {c.weapon}</div>
            </button>
          ))}
        </div>
        <button onClick={onBack} data-testid="class-pick-back" className="mt-6 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white">
          ← Back
        </button>
      </div>
    </div>
  );
}

// ────────────────────── Dungeon HUD ──────────────────────
function DungeonHUD({ run, heroClass, onExit }) {
  const hpPct = Math.round((run.player.hp / run.player.max_hp) * 100);
  const barrierUnlocked = run.questsCleared.includes("loot:pouch") &&
    run.mobs.length === 0;
  return (
    <div className="sticky top-0 z-30 bg-black/70 backdrop-blur border-b border-[#22C55E]/20 p-3 flex items-center gap-3">
      <button onClick={onExit} data-testid="dungeon-exit" className="text-xs text-neutral-500 hover:text-white">✕</button>
      <div className="text-2xl">{heroClass.glyph}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#22C55E]">Dream Forest · Lv 5 recommended</div>
        <div className="flex items-center gap-2 mt-0.5">
          <div className="h-1.5 bg-black border border-red-900 flex-1 max-w-xs overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-500 to-red-400 transition-all" style={{ width: `${hpPct}%` }} />
          </div>
          <span className="text-[10px] font-bold tabular-nums text-red-300">{run.player.hp}/{run.player.max_hp}</span>
        </div>
      </div>
      <div className="text-right text-[10px] uppercase tracking-widest text-neutral-500">
        <div data-testid="dungeon-mobs-left">Mobs: {run.mobs.length}</div>
        <div className={barrierUnlocked ? "text-[#FCD34D]" : "text-neutral-600"} data-testid="dungeon-barrier-status">
          Barrier: {barrierUnlocked ? "OPEN" : "locked"}
        </div>
        {/* 🛡️ Oct 2026 (Fix #25) — Block % pill. Was invisible in-dungeon;
            now it rides the HUD so the player can see what they earned. */}
        {typeof run.player.block === "number" && run.player.block > 0 && (
          <div className="text-[#38BDF8] font-bold" data-testid="dungeon-stat-block">
            🛡️ Block {run.player.block}%
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────── Scene + input ──────────────────────
function DreamForestScene({ run, setRun, heroClass, paused, bossUnlocked, onPrepRunFinish, onOpenLootBox, onEnterCombat, onEnterBoss }) {
  const sceneRef = useRef(null);
  const [joystick, setJoystick] = useState({ active: false, dx: 0, dy: 0 });
  const [tapTarget, setTapTarget] = useState(null);
  const keysRef = useRef(new Set());

  // Keep the latest callback refs so the proximity effect can stay
  // stable across parent re-renders (parent passes inline arrow fns).
  const onOpenLootBoxRef = useRef(onOpenLootBox);
  const onEnterCombatRef = useRef(onEnterCombat);
  const onEnterBossRef   = useRef(onEnterBoss);
  useEffect(() => { onOpenLootBoxRef.current = onOpenLootBox; }, [onOpenLootBox]);
  useEffect(() => { onEnterCombatRef.current = onEnterCombat; }, [onEnterCombat]);
  useEffect(() => { onEnterBossRef.current   = onEnterBoss;   }, [onEnterBoss]);

  // 🎯 Boss reveal — when the barrier drops (loot cleared + mobs dead), we
  // briefly spotlight Gorgonax. Not a cinematic cutscene, just a camera
  // hint so the player's eye tracks to the boss instead of wandering.
  const [bossRevealed, setBossRevealed] = useState(false);
  const wasBarrierOpen = useRef(false);

  // Input: WASD
  useEffect(() => {
    const onKey = (e, down) => {
      const k = e.key.toLowerCase();
      if (["w","a","s","d","arrowup","arrowdown","arrowleft","arrowright"].includes(k)) {
        if (down) keysRef.current.add(k); else keysRef.current.delete(k);
        e.preventDefault();
      }
    };
    const onKeyDown = (e) => onKey(e, true);
    const onKeyUp   = (e) => onKey(e, false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // Game tick — 12 fps for the movement loop. Paused when a modal is open
  // (combat, pouch pick, etc.) so the world freezes behind the modal.
  useEffect(() => {
    if (paused) return;
    const tick = setInterval(() => {
      setRun((rs) => {
        if (!rs) return rs;
        // ─── Player movement ───
        let dx = 0, dy = 0;
        if (keysRef.current.has("w") || keysRef.current.has("arrowup"))    dy -= 1;
        if (keysRef.current.has("s") || keysRef.current.has("arrowdown"))  dy += 1;
        if (keysRef.current.has("a") || keysRef.current.has("arrowleft"))  dx -= 1;
        if (keysRef.current.has("d") || keysRef.current.has("arrowright")) dx += 1;
        if (joystick.active) { dx += joystick.dx; dy += joystick.dy; }
        // Tap-to-move: only used if no active input
        if (!dx && !dy && tapTarget) {
          const ddx = tapTarget.x - rs.player.x;
          const ddy = tapTarget.y - rs.player.y;
          const d = Math.hypot(ddx, ddy);
          if (d < 0.4) setTapTarget(null);
          else { dx = ddx / d; dy = ddy / d; }
        }
        const speed = 0.3;
        const newX = clamp(rs.player.x + dx * speed, 0.5, ROOM.w - 0.5);
        const newY = clamp(rs.player.y + dy * speed, 0.5, ROOM.h - 0.5);
        const player = { ...rs.player, x: newX, y: newY };

        // ─── Mob AI ───
        const barrierOpen = rs.questsCleared.includes("loot:pouch") && rs.mobs.length === 0;
        const mobs = rs.mobs.map((m) => {
          const distToPlayer = dist(m, player);
          const distFromLeash = dist(m, m.leashOrigin);
          if (distToPlayer < m.aggroRadius && distFromLeash < m.leashRadius) {
            // Chase
            const ddx = player.x - m.x, ddy = player.y - m.y;
            const d = Math.hypot(ddx, ddy) || 1;
            const ms = 0.15;
            return { ...m, x: m.x + (ddx / d) * ms, y: m.y + (ddy / d) * ms, state: "chase" };
          }
          if (distFromLeash >= m.leashRadius) {
            // Returning
            const ddx = m.leashOrigin.x - m.x, ddy = m.leashOrigin.y - m.y;
            const d = Math.hypot(ddx, ddy) || 1;
            return { ...m, x: m.x + (ddx / d) * 0.15, y: m.y + (ddy / d) * 0.15, state: "returning" };
          }
          // Patrol
          const wp = m.patrol[m.patrolIdx];
          const ddx = wp.x - m.x, ddy = wp.y - m.y;
          const d = Math.hypot(ddx, ddy);
          if (d < 0.2) return { ...m, patrolIdx: (m.patrolIdx + 1) % m.patrol.length, state: "patrol" };
          return { ...m, x: m.x + (ddx / d) * 0.08, y: m.y + (ddy / d) * 0.08, state: "patrol" };
        });
        return { ...rs, player, mobs, barrierOpen };
      });
    }, 1000 / 12);
    return () => clearInterval(tick);
  }, [joystick, tapTarget, setRun, paused]);

  // ─── Proximity triggers ───
  // Paused when a modal (combat/pouch) is open so we don't fire duplicate
  // enter-combat calls while the modal is already up. Callback refs keep
  // this effect stable across parent re-renders (fixes the setState loop).
  useEffect(() => {
    if (paused || !run) return;
    // Loot box open at proximity — only trigger if fully closed (opened === false)
    if (run.lootBox.opened === false && dist(run.player, run.lootBox) < 1) {
      onOpenLootBoxRef.current?.();
      return;
    }
    // Mob contact → combat
    for (const m of run.mobs) {
      if (dist(m, run.player) < 0.9) { onEnterCombatRef.current?.(m); return; }
    }
    // Boss chamber entry (only if barrier open — gated by bossUnlocked)
    const barrierOpen = run.questsCleared.includes("loot:pouch") && run.mobs.length === 0 && bossUnlocked;
    if (barrierOpen && dist(run.player, run.bossArea) < 1) {
      onEnterBossRef.current?.();
    }
  }, [run, paused, bossUnlocked]);

  // Handle tap on the scene — supports both mouse click and touch.
  // On mobile browsers `onClick` has a small delay after touch. Wiring an
  // `onTouchEnd` handler that fires the same logic makes tap-to-move feel
  // instant on iOS/Android. `touch-none` on the scene stops the browser
  // from scrolling/zooming when the player is dragging inside the room.
  const setTargetFromEvent = (clientX, clientY) => {
    if (!sceneRef.current) return;
    const rect = sceneRef.current.getBoundingClientRect();
    const x = (clientX - rect.left) / TILE;
    const y = (clientY - rect.top) / TILE;
    setTapTarget({ x: clamp(x, 0.5, ROOM.w - 0.5), y: clamp(y, 0.5, ROOM.h - 0.5) });
  };
  const onSceneTap = (e) => {
    setTargetFromEvent(e.clientX, e.clientY);
  };
  const onSceneTouchEnd = (e) => {
    // Use `changedTouches` (touch that just ended). Avoids conflicting with
    // the joystick which lives outside the scene bounds.
    const t = e.changedTouches?.[0];
    if (!t) return;
    setTargetFromEvent(t.clientX, t.clientY);
    // Suppress the follow-up synthetic click that some browsers still fire.
    e.preventDefault();
  };

  // 🌲 Barrier open logic — kill mobs + loot cleared = barrier drops.
  // First-run gate: even when conditions are met, if `bossUnlocked` is
  // false we route to a "prep run" completion instead of unlocking the
  // boss chamber. Owner canon Mar 4, 2026.
  const barrierConditionsMet = run.questsCleared.includes("loot:pouch") && run.mobs.length === 0;
  const barrierOpen = barrierConditionsMet && bossUnlocked;

  // Fire prep-run completion the first time conditions are met on a
  // gated run. One-shot: the parent flips `paused=true` after we fire.
  useEffect(() => {
    if (barrierConditionsMet && !bossUnlocked) {
      onPrepRunFinish?.();
    }
  }, [barrierConditionsMet, bossUnlocked, onPrepRunFinish]);

  // Watch the barrier flipping open → trigger a 2.5s spotlight on Gorgonax.
  // Once the reveal finishes, it stays "revealed" so entering the boss
  // room isn't gated by the animation timer.
  useEffect(() => {
    if (barrierOpen && !wasBarrierOpen.current) {
      wasBarrierOpen.current = true;
      const t = setTimeout(() => setBossRevealed(true), 2500);
      return () => clearTimeout(t);
    }
  }, [barrierOpen]);
  const revealing = barrierOpen && !bossRevealed;

  return (
    <div className="relative">
      {/* Scene */}
      <div
        ref={sceneRef}
        onClick={onSceneTap}
        onTouchEnd={onSceneTouchEnd}
        data-testid="dream-forest-scene"
        className="relative mx-auto my-4 overflow-hidden cursor-pointer touch-none select-none"
        style={{
          width:  ROOM.w * TILE,
          height: ROOM.h * TILE,
          maxWidth: "100%",
          background:
            // 🌲 Layered forest floor — deep moss + firelight glow at the
            // barrier, dim green mist elsewhere. Replaces the plain radial
            // gradient placeholder.
            "radial-gradient(ellipse 60% 45% at 90% 50%, rgba(120,53,15,0.35) 0%, transparent 60%)," +
            "radial-gradient(ellipse 70% 60% at 20% 45%, rgba(34,197,94,0.10) 0%, transparent 65%)," +
            "linear-gradient(180deg, #0a1f16 0%, #071410 55%, #030807 100%)",
          boxShadow: "inset 0 0 180px rgba(4,25,15,0.9), inset 0 0 40px rgba(20,220,180,0.10)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* 🌲 Forest atmosphere — three parallax-ish layers behind gameplay */}
        <ForestAtmosphere />
        {/* Grid subtle — kept faint so tap-to-move still feels grid-y */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#22C55E 1px, transparent 1px), linear-gradient(90deg, #22C55E 1px, transparent 1px)",
            backgroundSize: `${TILE}px ${TILE}px`,
          }} />
        {/* Loot Box — Discovery-First: no label, tucked in a corner. Players
            find it by exploring, not by reading a floating waypoint. */}
        {!run.lootBox.opened && (
          <SceneSprite x={run.lootBox.x} y={run.lootBox.y} emoji="📦" muted testid="scene-loot-box" />
        )}
        {run.lootBox.opened === true && (
          <SceneSprite x={run.lootBox.x} y={run.lootBox.y} emoji="🎁" muted testid="scene-loot-box-opened" />
        )}
        {/* Mobs */}
        {run.mobs.map((m) => (
          <SceneSprite
            key={m.id}
            x={m.x}
            y={m.y}
            emoji={m.icon}
            state={m.state}
            label={`${m.name} Lv${m.lv}`}
            testid={`scene-mob-${m.id}`}
          />
        ))}
        {/* Barrier line at x=17 */}
        <div
          className="absolute inset-y-0 pointer-events-none transition-opacity"
          style={{
            left: 17 * TILE,
            width: 6,
            background: barrierOpen
              ? "linear-gradient(180deg, transparent, rgba(252,211,77,0.3), transparent)"
              : "linear-gradient(180deg, rgba(244,114,182,0.9), rgba(147,51,234,0.9), rgba(244,114,182,0.9))",
            boxShadow: barrierOpen ? "0 0 20px rgba(252,211,77,0.5)" : "0 0 40px rgba(244,114,182,0.8)",
            opacity: barrierOpen ? 0.3 : 1,
            animation: barrierOpen ? "none" : "shimmer 1.5s ease-in-out infinite",
          }}
          data-testid="scene-barrier"
        />
        <style>{`@keyframes shimmer { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.6); } }`}</style>
        {/* Gorgonax (visible behind barrier) */}
        <SceneSprite x={run.bossArea.x} y={run.bossArea.y} emoji="🐲" big label="" testid="scene-boss" />
        <SceneSprite x={run.bossArea.x - 1.5} y={run.bossArea.y + 1} emoji="🐣" label="" testid="scene-hatchback-1" />
        <SceneSprite x={run.bossArea.x + 1.5} y={run.bossArea.y + 1} emoji="🐣" label="" testid="scene-hatchback-2" />
        {/* Player */}
        <SceneSprite
          x={run.player.x}
          y={run.player.y}
          emoji={heroClass.glyph}
          big
          isPlayer
          testid="scene-player"
        />
        {/* Tap target ghost */}
        {tapTarget && (
          <div
            className="absolute rounded-full border-2 border-[#22C55E] pointer-events-none animate-ping"
            style={{
              left:  tapTarget.x * TILE - 8,
              top:   tapTarget.y * TILE - 8,
              width: 16, height: 16,
            }}
          />
        )}
        {/* 🎯 Boss reveal spotlight — 2.5s vignette focused on Gorgonax when
            the barrier drops for the first time. Everything except the boss
            chamber darkens; a ring pulses on the boss. */}
        {revealing && (
          <>
            <div
              className="absolute inset-0 pointer-events-none boss-reveal-vignette"
              style={{
                background: `radial-gradient(circle 180px at ${run.bossArea.x * TILE}px ${run.bossArea.y * TILE}px, transparent 0%, rgba(0,0,0,0.85) 100%)`,
                animation: "bossRevealFade 2.5s ease-out forwards",
              }}
              data-testid="boss-reveal-overlay"
            />
            <div
              className="absolute pointer-events-none"
              style={{
                left:  run.bossArea.x * TILE - 60,
                top:   run.bossArea.y * TILE - 60,
                width: 120, height: 120,
                borderRadius: "50%",
                border: "2px solid rgba(220,38,38,0.9)",
                boxShadow: "0 0 40px rgba(220,38,38,0.7), inset 0 0 40px rgba(220,38,38,0.4)",
                animation: "bossRevealRing 2.5s ease-out forwards",
              }}
              data-testid="boss-reveal-ring"
            />
            <div
              className="absolute pointer-events-none text-xs uppercase tracking-[0.4em] font-bold text-red-400"
              style={{
                left:  run.bossArea.x * TILE - 90,
                top:   run.bossArea.y * TILE - 100,
                textShadow: "0 0 20px rgba(220,38,38,0.9)",
                animation: "bossRevealTitle 2.5s ease-out forwards",
              }}
            ></div>
          </>
        )}
        <style>{`
          @keyframes bossRevealFade  { 0% { opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }
          @keyframes bossRevealRing  { 0% { transform: scale(0.4); opacity: 0; } 25% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 0; } }
          @keyframes bossRevealTitle { 0% { opacity: 0; letter-spacing: 0.1em; } 30% { opacity: 1; letter-spacing: 0.4em; } 100% { opacity: 0; } }
        `}</style>
      </div>
      {/* Joystick */}
      <Joystick onChange={setJoystick} />
    </div>
  );
}

// ────────────────────── Forest atmosphere (background layers) ──────────────────────
function ForestAtmosphere() {
  // Three stacked SVG layers behind the grid — tree canopy, undergrowth,
  // and drifting mist. All pointer-events-none so gameplay is unaffected.
  // Randomized positions are memo'd once so the trees don't jump on every
  // re-render.
  const trees = useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    id: i,
    x: (i * 71) % 100,
    y: 20 + ((i * 43) % 55),
    scale: 0.55 + ((i * 13) % 60) / 100,
    tint: i % 3 === 0 ? "#14532d" : (i % 3 === 1 ? "#0f3d24" : "#1e5a35"),
  })), []);
  const mist = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    id: i,
    x: (i * 17 + 10) % 90,
    y: (i * 31 + 12) % 80,
    w: 200 + ((i * 47) % 220),
    delay: i * 1.3,
  })), []);
  const mossPatches = useMemo(() => Array.from({ length: 22 }, (_, i) => ({
    id: i,
    x: (i * 53 + 7) % 100,
    y: (i * 29 + 41) % 100,
    r: 8 + ((i * 11) % 22),
  })), []);
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="forest-atmosphere">
      {/* Moss ground patches — sit closest to the floor */}
      <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none">
        {mossPatches.map((m) => (
          <ellipse
            key={m.id}
            cx={`${m.x}%`}
            cy={`${m.y}%`}
            rx={m.r}
            ry={m.r * 0.45}
            fill="#0f3d24"
            opacity={0.55}
          />
        ))}
      </svg>
      {/* Tree silhouettes — main foliage layer */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {trees.map((t) => (
          <g key={t.id} transform={`translate(${t.x} ${t.y * 0.75}) scale(${t.scale * 0.9})`}>
            {/* trunk */}
            <rect x="-0.6" y="5" width="1.2" height="3" fill="#3f2a13" opacity="0.9" />
            {/* canopy — 3 stacked ellipses for a chunky pine feel */}
            <ellipse cx="0" cy="4"   rx="4"   ry="3"   fill={t.tint} opacity="0.85" />
            <ellipse cx="0" cy="2"   rx="3"   ry="2.5" fill={t.tint} opacity="0.85" />
            <ellipse cx="0" cy="0.3" rx="2.2" ry="1.9" fill={t.tint} opacity="0.85" />
          </g>
        ))}
      </svg>
      {/* Drifting mist — top layer, gentle animation */}
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <radialGradient id="mistGrad">
            <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.10" />
            <stop offset="70%"  stopColor="#ffffff" stopOpacity="0.03" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.0" />
          </radialGradient>
        </defs>
        {mist.map((m) => (
          <ellipse
            key={m.id}
            cx={`${m.x}%`}
            cy={`${m.y}%`}
            rx={m.w}
            ry={m.w * 0.35}
            fill="url(#mistGrad)"
          >
            <animate attributeName="cx" values={`${m.x}%;${m.x + 8}%;${m.x}%`} dur={`${18 + m.id * 2}s`} repeatCount="indefinite" begin={`-${m.delay}s`} />
          </ellipse>
        ))}
      </svg>
      {/* Vignette — pull focus to the middle */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      />
    </div>
  );
}

function SceneSprite({ x, y, emoji, label, big, muted, pulse, isPlayer, state, testid }) {
  return (
    <div
      data-testid={testid}
      className={`absolute pointer-events-none transition-[left,top] duration-100 ease-linear ${
        pulse ? "animate-pulse" : ""
      } ${muted ? "opacity-40" : ""}`}
      style={{
        left: x * TILE - (big ? 24 : 18),
        top:  y * TILE - (big ? 24 : 18),
        fontSize: big ? 44 : 32,
        filter: state === "chase" ? "drop-shadow(0 0 8px #EF4444)" : (isPlayer ? "drop-shadow(0 0 8px #22C55E)" : "none"),
      }}
    >
      <div className="leading-none">{emoji}</div>
      {label && (
        <div className="text-[9px] text-white/70 text-center mt-0.5 whitespace-nowrap font-bold tabular-nums">{label}</div>
      )}
    </div>
  );
}

// ────────────────────── Virtual joystick ──────────────────────
function Joystick({ onChange }) {
  const [touch, setTouch] = useState(null);
  const baseRef = useRef(null);
  const startAt = useRef(null);
  const R = 60;

  const finish = () => { setTouch(null); startAt.current = null; onChange({ active: false, dx: 0, dy: 0 }); };
  const onStart = (e) => {
    const t = e.touches ? e.touches[0] : e;
    const rect = baseRef.current.getBoundingClientRect();
    startAt.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    setTouch({ x: t.clientX, y: t.clientY });
  };
  const onMove = (e) => {
    if (!startAt.current) return;
    const t = e.touches ? e.touches[0] : e;
    const dx = t.clientX - startAt.current.x;
    const dy = t.clientY - startAt.current.y;
    const d = Math.hypot(dx, dy);
    const clamped = d > R ? R : d;
    const nx = d ? (dx / d) * (clamped / R) : 0;
    const ny = d ? (dy / d) * (clamped / R) : 0;
    setTouch({ x: startAt.current.x + nx * R, y: startAt.current.y + ny * R });
    onChange({ active: d > 6, dx: nx, dy: ny });
  };

  return (
    <div className="fixed bottom-4 left-4 z-40 select-none touch-none" data-testid="joystick">
      <div
        ref={baseRef}
        onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={finish}
        onMouseDown={onStart} onMouseMove={(e) => touch && onMove(e)} onMouseUp={finish} onMouseLeave={finish}
        className="w-32 h-32 rounded-full bg-black/40 border-2 border-[#22C55E]/40 backdrop-blur-sm relative"
      >
        <div
          className="absolute w-14 h-14 rounded-full bg-[#22C55E]/70 border-2 border-[#22C55E]"
          style={{
            left: touch ? (touch.x - baseRef.current.getBoundingClientRect().left - 28) : 30,
            top:  touch ? (touch.y - baseRef.current.getBoundingClientRect().top  - 28) : 30,
          }}
        />
      </div>
      <div className="text-[9px] text-center text-neutral-500 mt-1 uppercase tracking-widest">Drag</div>
    </div>
  );
}

// ────────────────────── Chit Pouch modal ──────────────────────
// 🩹 Oct 2026 owner canon (Fix #26): the Chit Pouch pickup used to
// hand out BOTH +500 Chits AND the temp booster at the same time.
// Owner wants them SEPARATED — the player gets +150 Chits guaranteed
// plus a choice between a Lv 1 Temporal Booster OR +500 Chits (so
// the two pieces of loot aren't bundled). The modal below reflects
// that: mystery box opens → player picks ONE of the two.
function ChitPouchModal({ onPick, boost }) {
  const [revealed, setRevealed] = useState(false);
  const [picked,   setPicked]   = useState(null);  // 'chits' | 'booster'
  const pick = (choice) => {
    setPicked(choice);
    // bubble the choice up — parent handler updates runState accordingly.
    onPick(choice);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="chit-pouch-modal">
      <div className="bg-[#0F0F0F] border-2 border-[#FCD34D] max-w-md p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FCD34D]">💰 Chit Pouch</div>
        <div className="font-display text-xl mt-1">
          {!revealed ? "Open the mystery box:" : picked ? "Claimed." : "Pick one — not both:"}
        </div>
        {!revealed && (
          <div className="mt-4 text-[11px] text-neutral-400 leading-snug">
            Guaranteed: <b className="text-amber-300">+150 Chits</b>. Then pick ONE: more Chits or a Lv 1 Temporal Booster.
          </div>
        )}
        {revealed && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              data-testid="pouch-pick-chits"
              onClick={() => !picked && pick("chits")}
              disabled={picked && picked !== "chits"}
              className={`bg-black border-2 p-4 text-left transition ${
                picked === "chits" ? "border-amber-300 ring-2 ring-amber-400"
                : picked ? "border-neutral-700 opacity-40 cursor-not-allowed"
                : "border-[#FCD34D] hover:brightness-125"
              }`}
            >
              <div className="text-3xl">💰</div>
              <div className="font-bold text-sm mt-2">+500 Chits</div>
              <div className="text-[10px] text-neutral-500 mt-1">Guaranteed bag</div>
            </button>
            <button
              data-testid="pouch-pick-booster"
              onClick={() => !picked && pick("booster")}
              disabled={picked && picked !== "booster"}
              className={`bg-black border-2 p-4 text-left transition ${
                picked === "booster" ? "border-pink-400 ring-2 ring-pink-400"
                : picked ? "border-neutral-700 opacity-40 cursor-not-allowed"
                : "border-[#F472B6] hover:brightness-125"
              }`}
            >
              <div className="text-3xl">{boost?.icon || "🎁"}</div>
              <div className="font-bold text-sm mt-2">{boost?.label || "Lv 1 Temporal Booster"}</div>
              <div className="text-[10px] text-neutral-500 mt-1">{boost?.detail || "Temporary stat buff"}</div>
            </button>
          </div>
        )}
        {!revealed && (
          <button
            data-testid="pouch-claim"
            onClick={() => setRevealed(true)}
            className="w-full mt-4 bg-[#FCD34D] text-black font-bold py-2.5 uppercase tracking-widest text-xs hover:bg-[#F59E0B] transition"
          >
            Open Box
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────── Mini-combat modal ──────────────────────
function MiniCombatModal({ combat, setCombat, playerAtk, onWin, onDamage, onOverkill, onFlee }) {
  const [log, setLog] = useState([]);
  const [mobHp, setMobHp] = useState(combat.mobHp);
  const [minions, setMinions] = useState(combat.minions || []);
  // 💠 Local mana + cooldown state — skills fire client-side against the
  // room-local mob HP for now (proper shared-engine hook comes when
  // /api/dungeon/combat/* lands). Mana caps at 100 to match the arena.
  // Starts at 0 so the player must Generator-tap on turn 1 like every
  // other combat surface (canon Mar 5, 2026 — owner retired the 30-mana
  // free-pass so the loop feels honest).
  const [mana,    setMana]    = useState(0);
  const [cds,     setCds]     = useState({});
  const [turnCtr, setTurnCtr] = useState(0);
  // 💥 OVERKILL! popup — flashes on-screen for 1.2s when a killing blow
  // meets the ≥1.5×MaxHP threshold. Also bubbles up via onOverkill so the
  // dungeon run tracks the count for grade evaluation.
  const [overkillFlash, setOverkillFlash] = useState(false);

  // Compute a coarse damage multiplier from a skill's mana cost — big-cost
  // skills hit harder, generators (cost 0) hit for base. Damage_type "_multi"
  // adds a small variance. Matches the arena's spirit without shipping a
  // full second combat engine.
  const damageFor = (sk) => {
    const cost = Number(sk?.cost || 0);
    let mult = 1.0;
    if (cost === 0)      mult = 1.0;        // Generator
    else if (cost <= 25) mult = 1.4;        // Cheap active
    else if (cost <= 40) mult = 1.8;        // Heavy active
    else if (cost <= 55) mult = 2.2;        // Finisher
    else                 mult = 3.0;        // Ultimate
    // Multi-hit / ult variance
    if (sk?.damage_type?.endsWith("_multi") || sk?.bucket === "ultimates") {
      mult *= (0.9 + Math.random() * 0.3);
    } else {
      mult *= (0.85 + Math.random() * 0.3);
    }
    return Math.max(1, Math.round(playerAtk * mult));
  };

  const fireSkill = (pick) => {
    // pick is either "heavy"/"light" legacy string OR the enriched
    // SkillPicker payload `{move, skill_id, skill}` (Mar 5, 2026 fix —
    // the shared SkillPicker now includes the full skill object so the
    // damage/mana/log lines aren't undefined).
    let sk = pick;
    if (typeof pick === "string") {
      // Legacy fallback — treat heavy as cost 25, light as generator
      sk = pick === "heavy"
        ? { id: "_legacy_heavy", name: "Heavy Swing", icon: "⚔️", cost: 25, gain: 0 }
        : { id: "_legacy_light", name: "Light Swing", icon: "💢", cost: 0,  gain: 8 };
    } else if (pick && typeof pick === "object" && pick.skill) {
      sk = pick.skill;
    }
    // Enforce mana + cooldown client-side (SkillPicker already gates but be defensive).
    if ((sk.cost || 0) > mana) {
      setLog((l) => [`⚠️ Not enough mana for ${sk.name}.`, ...l].slice(0, 6));
      return;
    }
    const cdUntil = cds[sk.id] || 0;
    if (turnCtr < cdUntil) {
      setLog((l) => [`⚠️ ${sk.name} is on cooldown.`, ...l].slice(0, 6));
      return;
    }
    // Resolve player damage
    const dmg = damageFor(sk);
    const newMobHp = Math.max(0, mobHp - dmg);
    setMobHp(newMobHp);
    // 🐣 AoE splash — ultimates & _multi skills splash minions for 40% dmg.
    const isAoE = !!(sk?.damage_type?.endsWith("_multi") || sk?.bucket === "ultimates");
    if (isAoE && minions.length > 0) {
      const splashDmg = Math.max(1, Math.round(dmg * 0.4));
      const after = minions.map((m) => ({ ...m, hp: Math.max(0, m.hp - splashDmg) }));
      const dead = after.filter((m) => m.hp <= 0);
      const alive = after.filter((m) => m.hp > 0);
      setMinions(alive);
      const lines = [`🌪️ Splash hits minions for ${splashDmg} each.`,
                     ...dead.map((d) => `💥 ${d.name} is DOWN!`)];
      setLog((l) => [...lines.reverse(), ...l].slice(0, 6));
    }
    // Mana bookkeeping — spend then gain (generator refills, actives drain)
    setMana((m) => Math.min(100, Math.max(0, m - (sk.cost || 0) + (sk.gain || 0))));
    // Skill cooldown — small blanket 2-turn CD on cost > 0 skills so
    // rotations feel meaningful.
    if ((sk.cost || 0) > 0) {
      setCds((c) => ({ ...c, [sk.id]: turnCtr + 2 }));
    }
    setLog((l) => [
      `${sk.icon || "✨"} ${sk.name} — ${dmg} dmg to ${combat.mob.name}!`,
      ...l,
    ].slice(0, 6));

    // 💥 Overkill check — canon: killing_blow_dmg ≥ 1.5 × max_hp
    if (newMobHp <= 0) {
      const isOverkill = dmg >= (1.5 * combat.mob.max_hp);
      if (isOverkill) {
        setOverkillFlash(true);
        setLog((l) => [`💥 OVERKILL! ${dmg} dmg vs ${combat.mob.max_hp} MAX HP.`, ...l].slice(0, 6));
        onOverkill?.({ mob: combat.mob, dmg });
        setTimeout(() => setOverkillFlash(false), 1200);
      }
      setLog((l) => [`✅ ${combat.mob.name} defeated!`, ...l]);
      setTimeout(() => onWin(dmg, { overkill: isOverkill }), isOverkill ? 1300 : 400);
      return;
    }

    // Enemy counter — same tuning as before (boss telegraph at 30% HP)
    let enemyDmg = combat.mob.atk;
    if (combat.isBoss && (newMobHp / combat.mob.max_hp) < 0.3) {
      enemyDmg = Math.round(combat.mob.atk * 1.3);
      setLog((l) => [`💥 Gorgonax charges up... [tutorial-scale ultimate — barely stings]`, ...l].slice(0, 6));
    } else {
      setLog((l) => [`🩸 ${combat.mob.name} hits back for ${enemyDmg}!`, ...l].slice(0, 6));
    }
    // 🐣 Oct 2026 (Fix #24) — Hatchback minions ACTUALLY attack now.
    // Each surviving minion lands a chip hit of 25% of the boss's base atk.
    // Keeps them tactically relevant (ignore them → steady chip damage;
    // AoE them down → stop the chip).
    let totalEnemyDmg = enemyDmg;
    if (combat.isBoss && minions.length > 0) {
      const chip = Math.max(1, Math.round(combat.mob.atk * 0.25));
      totalEnemyDmg += chip * minions.length;
      setLog((l) => [
        `🐣 ${minions.length} Hatchback${minions.length > 1 ? "s" : ""} peck for ${chip * minions.length} chip!`,
        ...l,
      ].slice(0, 6));
    }
    onDamage(totalEnemyDmg);
    setTurnCtr((t) => t + 1);
  };

  const flee = () => { (onFlee || (() => setCombat(null)))(); };

  const mobHpPct = Math.round((mobHp / combat.mob.max_hp) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-3" data-testid="mini-combat-modal">
      <div className="bg-[#0F0F0F] border-2 border-red-500/60 max-w-md w-full p-5 relative">
        {/* 💥 OVERKILL! screen-fill popup */}
        {overkillFlash && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
            data-testid="mini-combat-overkill"
            style={{
              background: "radial-gradient(circle at 50% 50%, rgba(220,38,38,0.55) 0%, rgba(0,0,0,0.4) 60%)",
              animation: "pulse 0.4s ease",
            }}
          >
            <div
              className="font-display text-5xl sm:text-6xl font-black text-white tracking-[0.2em]"
              style={{ textShadow: "0 0 40px #DC2626, 0 0 12px #FFF" }}
            >
              💥 OVERKILL
            </div>
          </div>
        )}
        <div className="text-[10px] uppercase tracking-[0.3em] text-red-400">
          {combat.isBoss ? "⚔️ Boss Encounter" : "⚔️ Combat"}
        </div>
        <div className="text-center my-3">
          <div className="text-6xl mb-1">{combat.mob.icon}</div>
          <div className="font-bold">{combat.mob.name}</div>
          <div className="h-2 bg-black border border-red-900 max-w-xs mx-auto mt-2 overflow-hidden">
            <div className="h-full bg-red-500 transition-all" style={{ width: `${mobHpPct}%` }} />
          </div>
          <div className="text-[10px] text-red-300 tabular-nums mt-1">{mobHp} / {combat.mob.max_hp}</div>
          {combat.isBoss && minions.length > 0 && (
            <div className="flex justify-center gap-4 mt-3" data-testid="mini-combat-minion-row">
              {minions.map((h) => {
                const hpPct = Math.round((h.hp / 80) * 100);
                return (
                  <div key={h.id} className="text-center">
                    <div className="text-2xl">{h.icon}</div>
                    <div className="text-[8px]">{h.name}</div>
                    <div className="h-1 w-10 bg-black border border-red-900 mx-auto mt-0.5 overflow-hidden">
                      <div className="h-full bg-red-500 transition-all" style={{ width: `${hpPct}%` }} />
                    </div>
                    <div className="text-[8px] text-red-300 tabular-nums">{h.hp}/80</div>
                  </div>
                );
              })}
              <div className="text-[9px] text-amber-400 self-center italic max-w-[9ch]">
                AoE them → stop the chip
              </div>
            </div>
          )}
        </div>
        {/* 💠 Combat log — trimmed height so SkillPicker fits below */}
        <div className="bg-black/60 border border-zinc-900 p-2 h-20 overflow-y-auto text-[11px] leading-snug space-y-0.5 font-mono mb-2" data-testid="mini-combat-log">
          {log.length === 0
            ? <div className="text-neutral-600">Pick a skill to swing. Generators refill 💠 mana, Actives spend it.</div>
            : log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
        {/* ⚔️💠 SkillPicker — hooks straight into the arena kit endpoint so
            the exact same skills you'd see in a boss fight light up here. */}
        <SkillPicker
          fightId={`dream-forest-${combat.mob.id || combat.mob.name}`}
          mana={mana}
          manaCap={100}
          skillCooldowns={cds}
          currentTurn={turnCtr}
          kitEndpoint="/arena/skills"
          onPick={fireSkill}
          autoPickSecs={25}
        />
        <button
          data-testid="combat-flee"
          onClick={flee}
          className="w-full mt-2 bg-zinc-900 border border-zinc-700 text-white py-2 uppercase tracking-widest text-xs hover:border-zinc-500 transition"
        >
          🚪 Flee
        </button>
      </div>
    </div>
  );
}

// ────────────────────── Grade Screen ──────────────────────
function GradeScreen({ result, onExit }) {
  if (!result) return null;
  // 🌲 Prep-run variant (Mar 4, 2026) — the FIRST Dream Forest run
  // stops at the boss barrier. Route to a purpose-built completion
  // screen so the player understands *why* they didn't fight Gorgonax
  // and knows to return for the real encounter.
  if (result.prep_run) {
    return (
      <div className="min-h-screen bg-[#050B0A] text-white flex items-center justify-center p-4" data-testid="grade-screen-prep-run">
        <div className="max-w-md w-full text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Scouting Complete</div>
          <div className="mt-3 text-6xl">🌲</div>
          <div className="mt-4 font-display text-3xl leading-tight text-emerald-200" data-testid="prep-run-title">
            The Forest Remembers You
          </div>
          <p className="mt-4 text-sm text-neutral-300 leading-relaxed">
            You cleared the outer glade — mobs down, loot claimed. Return again when
            the forest is ready to open the way forward.
          </p>
          <div className="grid grid-cols-3 gap-2 text-center my-6">
            <Stat label="Mobs Cleared" value={result.totalKills} />
            <Stat label="HP Left"      value={`${Math.round(result.hpPct * 100)}%`} />
            <Stat label="Time"         value={`${result.timeSec.toFixed(1)}s`} />
          </div>
          <button
            data-testid="prep-run-exit"
            onClick={onExit}
            className="w-full bg-emerald-400 text-black font-bold py-3 uppercase tracking-widest text-sm hover:bg-emerald-300"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }
  const g = result.grade;
  const pool = GRADE_POOL[g];
  return (
    <div className="min-h-screen bg-[#050B0A] text-white flex items-center justify-center p-4" data-testid="grade-screen">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Dungeon Complete</div>
          <div className="mt-2 font-display text-[8rem] leading-none" style={{ color: pool.color, textShadow: `0 0 40px ${pool.color}88` }} data-testid="grade-letter">
            {g}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center mb-4">
          <Stat label="Overkill %" value={`${Math.round(result.overkillPct * 100)}%`} />
          <Stat label="HP Left"    value={`${Math.round(result.hpPct * 100)}%`} />
          <Stat label="Time"       value={`${result.timeSec.toFixed(1)}s`} />
        </div>
        <div className="bg-[#0F0F0F] border-2 p-4 text-center" style={{ borderColor: pool.color }}>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: pool.color }}>🎴 Card Draw · {pool.pool}</div>
          <div className="text-2xl font-bold mt-2" data-testid="grade-card">{pool.card}</div>
        </div>
        <div className="text-[10px] text-neutral-500 text-center mt-3">
          Overkills: {result.overkills} / {result.totalKills} mobs
          {g === "SSS" && <div className="text-[#FCD34D] mt-1">⭐ SSS MYTHIC+ · exclusive pool</div>}
        </div>
        <button data-testid="grade-exit" onClick={onExit} className="mt-6 w-full bg-white text-black font-bold py-3 uppercase tracking-widest text-sm hover:bg-neutral-200">
          Exit Dungeon
        </button>
      </div>
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="bg-[#0F0F0F] border border-zinc-900 py-2">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-bold tabular-nums">{value}</div>
    </div>
  );
}
